'use client';

import { generateEmailHash } from '@hyphengroup/cms-components/utils/emailHash';
import { sha256 } from 'js-sha256';
import uuid from 'react-uuid';
import { track } from 'utils/tracking';

/**
 * Data-layer tracking for the PL email-capture modal (CMS-1818).
 *
 * The event names and parameters follow the analytics team's tracking plan for
 * the "Personal Loan Lead Form" (Airtable #2461–#2465), which supersedes the
 * placeholder event names from the ticket body. Interactions not covered by that
 * plan (the old `popup-opened`, `redirect-fired`, `load-failure` and the
 * separate no-email submit) are intentionally dropped.
 *
 * Every event shares the lead-form identifiers below. Field/button interactions
 * are `form_interact`; `form_submit` fires once, only on a successful lead.
 */
const LEAD_FORM_NAME = 'hk-pl-lead-form';
const FORM_TYPE = 'lead form';
const VERTICAL_CODE = 'PL';

/** Fixed component titles from the tracking plan (#2461–#2463). */
const COMPONENT_TITLE = {
  email: 'Overlay/Personal Information_Email Address',
  consent: 'Overlay/Personal Information_Marketing Consent',
  close: 'Overlay/Header_Close',
} as const;

/** #2461 — user fills in the email field (fire once per open). */
export const trackEmailFieldFill = (): void => {
  track({
    event: 'form_interact',
    lead_form_name: LEAD_FORM_NAME,
    form_type: FORM_TYPE,
    action: 'fill',
    component_type: 'textbox',
    component_title: COMPONENT_TITLE.email,
    vertical_code: VERTICAL_CODE,
    // Never emit the address itself on an interaction event.
    form_field_value: '<<PII redacted>>',
  });
};

/** #2462 — user toggles the marketing-consent checkbox. */
export const trackConsentToggle = (checked: boolean): void => {
  track({
    event: 'form_interact',
    lead_form_name: LEAD_FORM_NAME,
    form_type: FORM_TYPE,
    action: checked ? 'check' : 'uncheck',
    component_type: 'checkbox',
    component_title: COMPONENT_TITLE.consent,
    vertical_code: VERTICAL_CODE,
    form_field_value: '',
  });
};

/** #2463 — user closes the overlay via the X (also backdrop / Esc dismiss). */
export const trackClose = (): void => {
  track({
    event: 'form_interact',
    lead_form_name: LEAD_FORM_NAME,
    form_type: FORM_TYPE,
    action: 'close',
    component_type: 'button',
    component_title: COMPONENT_TITLE.close,
    vertical_code: VERTICAL_CODE,
    form_field_value: '',
  });
};

/**
 * #2464 — user clicks the Skip button to continue without an email. The plan
 * titles it `Overlay/Footer_(dynamic - click_text)`, so the button's label is
 * interpolated.
 */
export const trackSkip = (clickText: string): void => {
  track({
    event: 'form_interact',
    lead_form_name: LEAD_FORM_NAME,
    form_type: FORM_TYPE,
    action: 'click',
    component_type: 'button',
    component_title: `Overlay/Footer_${clickText}`,
    vertical_code: VERTICAL_CODE,
    form_field_value: '',
  });
};

/** Product fields for the `items` array, mirroring `sendAnalytics` (#2465). */
export type EmailCaptureItem = {
  /** `item_id` — the product title, as `sendAnalytics` maps it. */
  itemId: string;
  itemName: string;
  itemBrand: string;
  /**
   * List position of the product. The modal's payload carries no position, so
   * callers pass '0' (single-product context) until a real index is threaded.
   */
  index: string | number;
};

/**
 * #2465 — successful lead submission. Fires only when consent is given and the
 * email is usable, so `marketing_consent` is always 'yes' and `user_data` is
 * always populated.
 *
 * The event carries only the hashed `user_data` block — never the raw address:
 * A&A confirmed the live MoEngage path matches on `email_hash_md5`, and the
 * shared `form_submit` dataLayer must stay PII-safe (#2465 QA). The consented
 * raw-email → MoEngage campaign path (ticket §17) is delivered off the dataLayer
 * by the CRM/Segment side, not from this event.
 */
export const trackFormSubmit = (params: {
  email: string;
  item: EmailCaptureItem;
}): void => {
  const { email, item } = params;

  track({
    event: 'form_submit',
    lead_form_name: LEAD_FORM_NAME,
    form_type: FORM_TYPE,
    action: 'click',
    vertical_code: VERTICAL_CODE,
    marketing_consent: 'yes',
    // Not a form.io form, so there is no server-generated submission id; mint one
    // per submission client-side, as `applyLeadFormSubmit` does (#2465 QA).
    form_submission_id: uuid(),
    items: [
      {
        item_id: item.itemId,
        item_name: item.itemName,
        item_brand: item.itemBrand,
        index: item.index,
      },
    ],
    user_data: {
      email_hash_md5: generateEmailHash(email),
      email_hash_sha256: sha256(email.toLowerCase()),
    },
  });
};
